/**
 * storeLoader: the gate on the store's own pages (docs/plans/taxes.md,
 * Decision 47, Tyler, 2026-10-01).
 *
 * WHAT IT DOES: everything vendorLoader does (signed in, identity verified,
 * a vendor), then one more question, the store's standing on GearGhost's
 * vendor terms. A store that has not accepted the version in force is sent
 * to Account Standing, where the New Vendor Terms banner opens the terms
 * (VendorSetupBanner), carrying the page it asked for as `?returnTo=`, so
 * accepting brings the vendor back to it.
 *
 * WHY HERE AND NOT ON THE PAGES: the backend refuses to publish a listing or
 * accept an order for a store that has not accepted, whatever a page shows.
 * Asking before a store page opens means a vendor meets the terms once, on
 * the way in, instead of at the end of a listing they just wrote. Orders and
 * messages are not store pages and stay open; an accept refused there goes
 * to the same row through useOpenVendorTermsOnRefusal.
 *
 * WHY NOT INSIDE createAuthLoader: that file decides who is signed in and
 * what role they hold, and is held to the auth tier's coverage floor. The
 * terms are a rule about a store, layered on top the way the Inventory
 * route's loader layers its prefetch.
 *
 * A standing that could not be read lets the page open, as a profile that
 * could not be read does (readForRouteChecks): the page's own requests
 * report the failure, and the backend still refuses what it must.
 *
 * Account Standing is not a store page (its route carries the verification
 * loader), so a store sent there is never sent on again.
 */
import { redirect, type LoaderFunctionArgs } from 'react-router'
import type { VendorTermsStanding } from '@gearghost/api-schemas/vendor'
import { vendorApi } from '@/redux/api/vendorApi'
import { readForRouteChecks, vendorLoader } from '@/navigation/loaders/createAuthLoader'
import { PATHS } from '@/navigation/paths'
import { withReturnTo } from '@/navigation/utils/returnTo'

/** Account Standing, carrying the page this request asked for as the way back. */
const standingPageFor = (request: Request): string => {
  const url = new URL(request.url)
  return withReturnTo(PATHS.dashboard.accountStanding, url.pathname + url.search + url.hash)
}

export const storeLoader = async ({ request }: Pick<LoaderFunctionArgs, 'request'>) => {
  const allowed = await vendorLoader({ request })
  // The gate's own redirect (sign in, verify, not a vendor) comes first.
  if (allowed instanceof Response) return allowed
  // No profile to check against: the gate let the page load without its
  // checks, and there is no store known to ask about.
  if (allowed === null || allowed.user === null) return allowed

  const standing: VendorTermsStanding | null = await readForRouteChecks(
    vendorApi.endpoints.getVendorTermsStanding,
    'vendor terms standing',
  )
  if (standing !== null && !standing.accepted) return redirect(standingPageFor(request))

  return allowed
}
